import { useEffect, useRef, useState } from "react";
import { beatPresence, leaveReview, type PresentPerson } from "../../../api/content/reviewCollaborationApi";

/** A heartbeat every 15 seconds; the server forgets a page after 45 (three missed beats). */
export const HEARTBEAT_MILLISECONDS = 15_000;
const FOCUS_SETTLE_MILLISECONDS = 400;

export interface ReviewPresenceState {
  /** Who has the review open, the viewer too, in order of arrival. */
  readonly present: readonly PresentPerson[];
  /** The change set has another head than the one shown: the page offers a reload. */
  readonly newHead: boolean;
}

/**
 * Presence on an open review (curation-ui.md CU-10): a heartbeat names the unit in focus
 * and answers who else is here, the head, and an activity stamp. When others decide,
 * mark, resolve or ask on the same head, `onActivity` reloads quietly; a new head is only
 * offered, never loaded under the reviewer's feet. A hidden tab sends no heartbeat.
 */
export function useReviewPresence(changeSetId: string, unitKey: string | null, shownHead: string | null, onActivity: () => void): ReviewPresenceState {
  const [state, setState] = useState<ReviewPresenceState>({ present: [], newHead: false });
  const activity = useRef<string | null>(null);
  const reload = useRef(onActivity);
  useEffect(() => {
    reload.current = onActivity;
  });

  useEffect(() => {
    if (shownHead === null) {
      return;
    }
    const controller = new AbortController();
    const beat = () => {
      if (document.hidden) {
        return;
      }
      beatPresence(changeSetId, unitKey, controller.signal).then(
        (answer) => {
          const moved = answer.head !== shownHead;
          if (!moved && activity.current !== null && activity.current !== answer.activity) {
            reload.current();
          }
          activity.current = answer.activity;
          setState({ present: answer.present, newHead: moved });
        },
        () => {
          // Presence is a courtesy: a missed heartbeat shows nobody, and the next one retries.
        },
      );
    };
    // Stepping through units with j and k sends one heartbeat for where the reviewer stops.
    const first = window.setTimeout(beat, FOCUS_SETTLE_MILLISECONDS);
    const timer = window.setInterval(beat, HEARTBEAT_MILLISECONDS);
    document.addEventListener("visibilitychange", beat);
    return () => {
      controller.abort();
      window.clearTimeout(first);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", beat);
    };
  }, [changeSetId, unitKey, shownHead]);

  // Leaving the review (not just another unit) tells the server at once.
  useEffect(
    () => () => {
      leaveReview(changeSetId).catch(() => undefined);
    },
    [changeSetId],
  );

  return state;
}
